import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import { DefaultValues, ToggleValues } from './types';
import { UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const { Box, Button, Title, Table, TextField, Toggle } = vp;

type UpdateWebhookProps = {
  defaultValues: DefaultValues;
  toggleValues: ToggleValues;
  setDefaultValues: React.Dispatch<React.SetStateAction<DefaultValues>>;
  setToggleValues: React.Dispatch<React.SetStateAction<ToggleValues>>;
  onSubmit: () => void;
};
type FC = FunctionComponent<UpdateWebhookProps>;

const UpdateWebhook: FC = (props: UpdateWebhookProps) => {
  const { defaultValues, toggleValues, setDefaultValues, setToggleValues, onSubmit } = props;
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true);
  const [validatedWebhookEventsList, setValidatedWebhookEventsList] = useState<vp.WebhookEvent[]>([]);
  const updateWebhookRows = useBuildUpdateWebhookRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks.Update' });
  const { t: tWebhooks } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const toggleHandleChange = (id: vp.WebhookEvent) => {
    setToggleValues((prevValues) => ({
      ...prevValues,
      [id]: !prevValues[id],
    }));
  };

  useEffect(() => {
    Object.entries(toggleValues).forEach(([key, value]) => {
      if (value === false) {
        defaultValues[key as keyof DefaultValues] = '';
      }
    });
  }, [toggleValues]);

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      onSubmit={() => {
        onSubmit();
      }}
      validateOnChange
      validateOnBlur
      validationSchema={createdValidationSchema()}
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        useEffect(() => {
          setDefaultValues(values);
        }, [values]);

        useEffect(() => {}, [errors]);

        useEffect(() => {
          const hasErrors = Object.keys(errors).length !== 0;
          const missingValues = Object.entries(toggleValues).some(
            ([key, value]) => value && defaultValues[key as keyof DefaultValues] === '',
          );

          setButtonIsDisabled(missingValues || hasErrors);
        }, [toggleValues, defaultValues, errors]);

        useEffect(() => {
          const changedKeys = getChangedWebhookEvent(values, defaultValues);
          const updatedWebhookEventsList = validatedWebhookEventsList.filter((event) => !changedKeys.includes(event));
          setValidatedWebhookEventsList(updatedWebhookEventsList);
        }, [values]);

        const updateWebhookTable: vp.TableProps = {
          name: 'update-webhook-fields',
          columns: {
            Field: { header: t('Form.event') },
            Toggle: { header: t('Form.enable'), align: 'left' },
            Input: { header: t('Form.url') },
          },
          rows: updateWebhookRows.map(({ label, id, preset }) => ({
            Field: label,
            Toggle: (
              <Box flx="row" jc="fs" ps="sm">
                <Toggle
                  name={id}
                  onBlur={handleBlur}
                  onChange={() => toggleHandleChange(id)}
                  value={toggleValues[id]}
                />
              </Box>
            ),
            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                placeholder={t('Form.urlPlaceholder')}
                onBlur={handleBlur}
                disabled={!toggleValues[id]}
                onChange={handleChange}
                value={defaultValues[id as keyof DefaultValues]}
                error={errors[id] && touched[id] ? errors[id] : null}
              />
            ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Box flx="col" mb="md" gapSize="16px">
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  if (history.length > 1) {
                    history.goBack();
                  } else {
                    history.replace('/settings/webhooks');
                  }
                }}
              >
                {history.length > 1 ? tGlobal('Buttons.back') : tWebhooks('pageTitle')}
              </UtilButton>
              <Title title={t('pageTitle')} />
            </Box>
            <Table {...updateWebhookTable} />
            <Box mt="md" jc="fe">
              <Button
                id="Webhook-review"
                type="submit"
                label={tGlobal('Buttons.review')}
                isDisabled={buttonIsDisabled}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      bankaccount: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      transaction: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      paylink: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      batchrequest: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      batchdetail: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      accountonboarding: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      scheduledtransactions: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      transactiongroup: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      accountverifications: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      accountbalance: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      clientaccountbalance: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      paymentreceived: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
      accountlimit: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        t('Form.invalidURLValidation'),
      ),
    });
  }
};

function getChangedWebhookEvent(values: DefaultValues, defaultValues: DefaultValues): vp.WebhookEvent[] {
  const changedKeys = (Object.keys(values) as (keyof DefaultValues)[]).filter(
    (key) => values[key] !== defaultValues[key],
  );

  return changedKeys;
}

export default UpdateWebhook;
